import React, { useState } from "react";
import { ChevronDown, Plus, Minus } from "lucide-react";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";
import Button from "../common/Button";
import { useBookingModal } from "../../contexts/BookingModalContext";

const faqs = [
  {
    question: "How long will my project take?",
    answer:
      "We understand you want to move in or start using your space as soon as possible. Rental home packages are ready in 20 days, while full-home projects take 45 days. This includes everything from design to installation. We'll keep you updated throughout the process so you always know what's happening.",
  },
  {
    question: "Do you work in my area?",
    answer:
      "Yes! We serve all of Hyderabad including Gachibowli, Kondapur, Madhapur, Narsingi, Banjara Hills, Jubilee Hills, Kukatpally, Miyapur, Manikonda, and surrounding areas. Our centrally located factory means we can reach you efficiently, no matter where you are in the city.",
  },
  {
    question: "What materials do you use? I'm worried about quality.",
    answer:
      "We understand your concern – you want materials that last. We don't use MDF. Instead, we use MR Ply for dry areas, BWR Ply for semi-wet spaces, BWP Ply for kitchens and wet areas, and HDHMR where you need extra strength. These materials are chosen because they last, and we want your investment to stand the test of time.",
  },
  {
    question: "What if something goes wrong?",
    answer:
      "We stand behind our work. We provide a 10-year warranty on materials and construction, and 5 years on hardware. If something isn't right, we'll fix it. Your peace of mind matters to us.",
  },
  {
    question: "I'm a builder/architect. Can you handle volume projects?",
    answer:
      "Absolutely. We work with many builders and architects, and we understand you need reliability and scale. We handle bulk kitchens, model homes, clubhouses, doors, and custom job works. Our factory and processes are built to deliver consistent quality at scale, on time.",
  },
  {
    question: "I'm renting. What options do I have?",
    answer:
      "We get it – you want a nice space but don't want to over-invest in a rental. Our rental packages start at \u20b93 Lakhs for a 2BHK and include everything you need: modular kitchen, wardrobes, and essential furniture. Same quality materials, smart designs that work for rental living, ready in 20 days.",
  },
  {
    question: "Can I see where my interiors are being made?",
    answer:
      "Of course! We love showing people our factory. Seeing where your home is being crafted helps you understand the care we put into every piece. Visit our 10,000+ sq. ft facility, see our processes, and meet the team. Just reach out to schedule a tour.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  const { openModal } = useBookingModal();

  return (
    <section className="section-spacing bg-background-main">
      <div className="container-custom section-padding">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - FAQ */}
          <div>
            <SectionHeading
              label="We're Here to Help"
              title="Common questions, honest answers."
              align="left"
            />

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <AnimatedSection key={index} delay={index * 50}>
                  <div
                    className={`bg-white rounded-2xl overflow-hidden transition-shadow ${
                      openIndex === index ? "shadow-medium" : "shadow-soft"
                    }`}
                  >
                    <button
                      onClick={() =>
                        setOpenIndex(openIndex === index ? -1 : index)
                      }
                      className="w-full px-6 py-5 flex items-center justify-between text-left"
                    >
                      <span className="font-medium text-brand-primary pr-4">
                        {faq.question}
                      </span>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                          openIndex === index
                            ? "bg-brand-accent text-white"
                            : "bg-background-card text-brand-primary"
                        }`}
                      >
                        {openIndex === index ? (
                          <Minus className="w-4 h-4" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openIndex === index ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <p className="px-6 pb-5 text-text-secondary leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>

          {/* Right - CTA */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <AnimatedSection animation="fade-left">
              <div className="bg-gradient-brand rounded-3xl p-8 lg:p-10 text-white">
                <h3 className="font-heading text-h3 mb-4 text-white">
                  Let's talk about your space
                </h3>
                <p className="text-white/80 mb-8 leading-relaxed">
                  We'd love to understand what you're looking for. Book a
                  consultation and let's explore what's possible for your home.
                  No pressure, just a conversation about making your space work
                  better for you.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button onClick={openModal} variant="white">
                    Book Consultation
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
}
